export type PersistenceCategory =
  | "preference"
  | "workspace"
  | "draft"
  | "result-cache"
  | "scroll"
  | "contact-config"
  | "device-preference"
  | "coordination"
  | "legacy";

export interface PersistencePolicy {
  id: string;
  category: PersistenceCategory;
  scope: "user" | "device" | "coordination" | "legacy";
  version: number;
  ttlMs: number;
  maxBytes: number;
  privacy: "private" | "non-sensitive" | "coordination";
  clearOnLocalData: boolean;
  retainVisibleStateAfterClear?: boolean;
  schema: string;
  defaultValue: unknown;
  migration: string;
  clearOn: readonly ("logout-invalidation" | "explicit-local-clear" | "successful-action" | "ttl" | "never")[];
  conflict: "newest-write" | "reject" | "server-version";
  durability: "durable-with-visible-memory-fallback" | "session-only" | "coordination";
  validate?: (value: unknown) => boolean;
}

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const PERSISTENCE_TTLS = {
  preference: 180 * DAY,
  workspace: 12 * HOUR,
  draft: 7 * DAY,
  resultCache: 10 * MINUTE,
  scroll: 2 * HOUR,
  // User-authored outbound identity is a durable preference, not a cache.
  // It is explicitly removed on logout/local clear instead of silently
  // changing the sender identity after an arbitrary idle interval.
  contactConfig: Number.POSITIVE_INFINITY,
  devicePreference: 365 * DAY,
} as const;

const isBoolean = (value: unknown): value is boolean => typeof value === "boolean";
const isString = (value: unknown): value is string => typeof value === "string";
const isNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);
const oneOf = <T extends string | number>(...values: T[]) =>
  (value: unknown): value is T => values.includes(value as T);
export const isPropertiesFilterState = (value: unknown): boolean => {
  if (!value || typeof value !== "object") return false;
  const filter = value as Record<string, unknown>;
  const strings = (candidate: unknown) =>
    Array.isArray(candidate) && candidate.every((item) => typeof item === "string");
  const nullableBoolean = (candidate: unknown) =>
    candidate === null || typeof candidate === "boolean";
  return typeof filter.search === "string" &&
    strings(filter.neighborhoods) &&
    strings(filter.propertyManagement) &&
    nullableBoolean(filter.hasPetPolicy) &&
    nullableBoolean(filter.hasSpecials) &&
    nullableBoolean(filter.hasCommission) &&
    typeof filter.showFavoritesOnly === "boolean" &&
    typeof filter.showActiveOnly === "boolean" &&
    typeof filter.noFloorPlanImage === "boolean";
};

export const isSearchResultSnapshot = (value: unknown): boolean =>
  Array.isArray(value) && value.every((row) => {
    if (!row || typeof row !== "object" || Array.isArray(row)) return false;
    const result = row as Record<string, unknown>;
    const optionalString = (key: string) =>
      result[key] === undefined || result[key] === null ||
      typeof result[key] === "string";
    const optionalNumber = (key: string) =>
      result[key] === undefined || result[key] === null ||
      (typeof result[key] === "number" && Number.isFinite(result[key]));
    const optionalBoolean = (key: string) =>
      result[key] === undefined || result[key] === null ||
      typeof result[key] === "boolean";
    return typeof result.property_id === "string" &&
      result.property_id.length > 0 &&
      typeof result.property_name === "string" &&
      [
        "neighborhood", "model_name", "unit_number", "status",
        "availability_date", "s3_image_url", "floorplan_image_url",
        "floor_plan_id", "property_website", "url", "specials",
        "specials_summary", "property_management",
        "inventory_contract_version", "inventory_freshness_state",
        "inventory_last_verified_at", "inventory_freshness_badge",
        "inventory_serve_note",
      ].every(optionalString) &&
      [
        "bedrooms", "bathrooms", "square_feet", "current_price",
        "price_per_sqft", "latitude", "longitude", "distance_miles",
      ].every(optionalNumber) &&
      ["lambda_compatible", "inventory_serve_pricing"].every(optionalBoolean);
  });
export const isFingerprintedSearchResultSnapshot = (value: unknown): boolean => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const snapshot = value as Record<string, unknown>;
  return typeof snapshot.criteriaFingerprint === "string" &&
    snapshot.criteriaFingerprint.length > 0 &&
    snapshot.criteriaFingerprint.length <= 4096 &&
    isSearchResultSnapshot(snapshot.rows);
};

const preference = (
  id: string,
  schema: string,
  defaultValue: unknown,
  validate: (value: unknown) => boolean = () => true,
  maxBytes = 8 * 1024
): PersistencePolicy => ({
  id,
  category: "preference",
  scope: "user",
  version: 2,
  ttlMs: PERSISTENCE_TTLS.preference,
  maxBytes,
  privacy: "private",
  clearOnLocalData: true,
  schema,
  defaultValue,
  migration: "exact-user v1 -> generation-fenced v2; drop other incompatible versions",
  clearOn: ["logout-invalidation", "explicit-local-clear", "ttl"],
  conflict: "newest-write",
  durability: "durable-with-visible-memory-fallback",
  validate,
});

const workspace = (
  id: string,
  schema: string,
  defaultValue: unknown,
  validate: (value: unknown) => boolean = () => true,
  maxBytes = 16 * 1024
): PersistencePolicy => ({
  id,
  category: "workspace",
  scope: "user",
  version: 2,
  ttlMs: PERSISTENCE_TTLS.workspace,
  maxBytes,
  privacy: "private",
  clearOnLocalData: true,
  schema,
  defaultValue,
  migration: "exact-user v1 -> generation-fenced v2; drop other incompatible versions",
  clearOn: ["logout-invalidation", "explicit-local-clear", "ttl"],
  conflict: "newest-write",
  durability: "durable-with-visible-memory-fallback",
  validate,
});

/**
 * Authoritative logical-key registry for usePersistedState. Its key union is
 * also the hook's input type, so a new call site cannot persist an unregistered
 * literal without failing TypeScript.
 */
export const PERSISTED_STATE_POLICIES = {
  "settings.activeTabKey": {
    ...workspace(
      "settings-active-tab",
      "one of profile|integrations|email|templates|botFlows|security|data|systemGuide",
      "profile",
      oneOf("profile", "integrations", "email", "templates", "botFlows", "security", "data", "systemGuide")
    ),
    retainVisibleStateAfterClear: true,
  },
  "admin.showAllRuns": preference("admin-show-all-runs", "boolean", false, isBoolean),
  "admin.healthSearch": workspace("admin-health-search", "string", "", isString),
  "dashboard.timeRange": preference("dashboard-time-range", "7d|30d|90d", "7d", oneOf("7d", "30d", "90d")),
  "dashboard.showPerformers": preference("dashboard-show-performers", "boolean", false, isBoolean),
  "dashboard.chartMode": preference("dashboard-chart-mode", "leads|activity", "leads", oneOf("leads", "activity")),
  "dashboard.showSearches": preference("dashboard-show-searches", "boolean", false, isBoolean),
  "dashboard.showAllAttention": preference("dashboard-show-all-attention", "boolean", false, isBoolean),
  "dashboard.showAllInvoices": preference("dashboard-show-all-invoices", "boolean", false, isBoolean),
  "properties.viewMode": preference("properties-view-mode", "grid|list", "grid", oneOf("grid", "list")),
  "properties.filters": workspace("properties-filters", "validated FilterState", {
    search: "", neighborhoods: [], propertyManagement: [], hasPetPolicy: null,
    hasSpecials: null, hasCommission: null, showFavoritesOnly: false,
    showActiveOnly: true, noFloorPlanImage: false,
  }, isPropertiesFilterState, 64 * 1024),
  // Ch78 · the default stays "active": `/?page=clients` is a canonical URL that
  // e2e (open-client-restore, client-id-synchronization) pins to show the
  // client roster. Leads-first is one tap away and the choice persists.
  // Ch78 · Grant (2026-09-10): "make incoming leads the landing tab for the
  // Leads page". The top of the funnel is what goes stale fastest, so it is
  // what the page should open on.
  "clients.mainTab": workspace("clients-main-tab", "leads|active|out_of_contact", "leads", oneOf("leads", "active", "out_of_contact")),
  "clients.viewMode": preference("clients-view-mode", "grid|list|pipeline|needs_touch", "grid", oneOf("grid", "list", "pipeline", "needs_touch")),
  "clients.searchQuery": workspace("clients-search-query", "string", "", isString),
  "clients.statusFilter": workspace("clients-status-filter", "known client status", "all", isString),
  "clients.moveInWindow": workspace("clients-move-in-window", "all|30|60|90", "all", oneOf("all", "30", "60", "90")),
  "clientDetail.activeTab": workspace("client-detail-active-tab", "non-negative tab index", 0, (value) => isNumber(value) && value >= 0 && Number.isInteger(value)),
  "marketInsights.pricingGroupBy": preference("market-pricing-group", "neighborhood|bedrooms|sqft_range|property", "neighborhood", oneOf("neighborhood", "bedrooms", "sqft_range", "property")),
  "marketInsights.pricingHistoryGroupBy": preference("market-history-group", "property|floor_plan|bedrooms|sqft_range", "bedrooms", oneOf("property", "floor_plan", "bedrooms", "sqft_range")),
  "marketInsights.pricingHistoryMetric": preference("market-history-metric", "avg_price|min_price|max_price|avg_price_per_sqft", "avg_price", oneOf("avg_price", "min_price", "max_price", "avg_price_per_sqft")),
  "marketInsights.pricingHistoryDaysBack": preference("market-history-days", "positive integer <= 3650", 90, (value) => isNumber(value) && Number.isInteger(value) && value > 0 && value <= 3650),
  "marketInsights.includeAvailableNow": preference("market-include-available", "boolean", false, isBoolean),
  "marketInsights.sortBy": preference("market-sort-by", "available_now|avg_price|name|rating|total_units", "available_now", oneOf("available_now", "avg_price", "name", "rating", "total_units")),
  "marketInsights.sortOrder": preference("market-sort-order", "asc|desc", "desc", oneOf("asc", "desc")),
  "messages.railCollapsed": preference("messages-rail-collapsed", "boolean", false, isBoolean),
  // Ch78 · Messages shows ENGAGED clients by default (Grant's IA ruling).
  // Ch78 · WHOSE clients Messages shows: "mine" (default) · "all" · an agent id.
  "messages.queueAgent": preference("messages-queue-agent", "mine|all|<profile id>", "mine", isString),
  // Ch78 · the Move-in Board's zoom and lanes.
} as const satisfies Record<string, PersistencePolicy>;

export type PersistedStateKey = keyof typeof PERSISTED_STATE_POLICIES;

export function persistedStatePolicy(key: PersistedStateKey): PersistencePolicy {
  return PERSISTED_STATE_POLICIES[key];
}

export const SEARCH_DRAFT_POLICY: PersistencePolicy = {
  id: "search-work-draft",
  category: "draft",
  scope: "user",
  version: 2,
  ttlMs: PERSISTENCE_TTLS.draft,
  maxBytes: 128 * 1024,
  privacy: "private",
  clearOnLocalData: true,
  schema: "Private Search criteria (including bounded proximity address), selections, and compose state; no result rows",
  defaultValue: null,
  migration: "v1 combined Search draft -> v2 compact draft + result snapshot",
  clearOn: ["logout-invalidation", "explicit-local-clear", "ttl"],
  conflict: "newest-write",
  durability: "durable-with-visible-memory-fallback",
};

export const SEARCH_RESULT_POLICY: PersistencePolicy = {
  id: "search-result-snapshot",
  category: "result-cache",
  scope: "user",
  version: 2,
  ttlMs: PERSISTENCE_TTLS.resultCache,
  maxBytes: 1024 * 1024,
  privacy: "private",
  clearOnLocalData: true,
  schema: "executed-criteria fingerprint plus bounded array of Search result rows",
  defaultValue: null,
  migration: "drop unfingerprinted/incompatible versions and regenerate from Search criteria",
  clearOn: ["logout-invalidation", "explicit-local-clear", "ttl"],
  conflict: "newest-write",
  durability: "durable-with-visible-memory-fallback",
  validate: isFingerprintedSearchResultSnapshot,
};

export const SEARCH_SCROLL_POLICY: PersistencePolicy = {
  id: "search-scroll",
  category: "scroll",
  scope: "user",
  version: 1,
  ttlMs: PERSISTENCE_TTLS.scroll,
  maxBytes: 1024,
  privacy: "private",
  clearOnLocalData: true,
  schema: "finite non-negative scroll offset",
  defaultValue: 0,
  migration: "drop raw pre-registry offsets",
  clearOn: ["logout-invalidation", "explicit-local-clear", "ttl"],
  conflict: "newest-write",
  durability: "durable-with-visible-memory-fallback",
  validate: (value) => isNumber(value) && value >= 0,
};

export const EMAIL_CONFIG_POLICY: PersistencePolicy = {
  id: "email-contact-config",
  category: "contact-config",
  scope: "user",
  version: 2,
  ttlMs: PERSISTENCE_TTLS.contactConfig,
  maxBytes: 16 * 1024,
  privacy: "private",
  clearOnLocalData: true,
  schema: "owner-bound agent name, email, phone, and job title",
  defaultValue: "profile-derived memory default",
  migration: "owner-bound v1 -> generation-fenced v2; ownerless key is dropped",
  clearOn: ["logout-invalidation", "explicit-local-clear"],
  conflict: "newest-write",
  durability: "durable-with-visible-memory-fallback",
};

export const THEME_POLICY: PersistencePolicy = {
  id: "device-theme",
  category: "device-preference",
  scope: "device",
  version: 1,
  ttlMs: PERSISTENCE_TTLS.devicePreference,
  maxBytes: 128,
  privacy: "non-sensitive",
  clearOnLocalData: false,
  schema: "light|dark",
  defaultValue: "dark",
  migration: "raw light|dark -> registered envelope",
  clearOn: ["ttl"],
  conflict: "newest-write",
  durability: "durable-with-visible-memory-fallback",
  validate: oneOf("light", "dark"),
};

export const WALKTHROUGH_SESSION_POLICY: PersistencePolicy = {
  id: "demo-walkthrough-session",
  category: "workspace",
  scope: "device",
  version: 1,
  ttlMs: Number.POSITIVE_INFINITY,
  maxBytes: 16,
  privacy: "non-sensitive",
  clearOnLocalData: true,
  schema: "session-only boolean completion flag",
  defaultValue: "false",
  migration: "none",
  clearOn: ["logout-invalidation", "explicit-local-clear"],
  conflict: "newest-write",
  durability: "session-only",
  validate: oneOf("true", "false"),
};

export const DEVICE_THEME_KEY = "lnpro.themeMode";
export const WALKTHROUGH_SESSION_KEY = "demo_walkthrough_completed";
export const LEGACY_LOCAL_DATA_KEYS = [
  "app_preferences",
  "notification_settings",
  "email_config",
  "messages.railCollapsed",
] as const;

export const activeUserPayloadPrefixes = (userId: string): string[] => [
  `persist:${userId}:`,
  `sms_send:${userId}:`,
  `search_draft:${userId}:`,
  `search_result:${userId}:`,
  `scroll:${userId}:`,
];

export function isActiveUserPayloadKey(key: string, userId: string): boolean {
  return activeUserPayloadPrefixes(userId).some((prefix) => key.startsWith(prefix));
}

export function classifyPersistenceKey(
  key: string,
  userId: string
): PersistencePolicy | null {
  if (key === DEVICE_THEME_KEY) return THEME_POLICY;
  if ((LEGACY_LOCAL_DATA_KEYS as readonly string[]).includes(key)) {
    return {
      id: `legacy-${key}`,
      category: "legacy",
      scope: "legacy",
      version: 0,
      ttlMs: 0,
      maxBytes: 0,
      privacy: "private",
      clearOnLocalData: true,
      schema: "retired ownerless key",
      defaultValue: null,
      migration: "drop; ownership cannot be inferred",
      clearOn: ["explicit-local-clear"],
      conflict: "reject",
      durability: "durable-with-visible-memory-fallback",
    };
  }
  if (key.startsWith("ln:persistence:")) {
    return {
      id: "persistence-coordination",
      category: "coordination",
      scope: "coordination",
      version: 1,
      ttlMs: Number.POSITIVE_INFINITY,
      maxBytes: 64 * 1024,
      privacy: "coordination",
      clearOnLocalData: false,
      schema: "active identity, generation, logout event, or key tombstone",
      defaultValue: null,
      migration: "none; preserve or advance",
      clearOn: ["never"],
      conflict: "reject",
      durability: "coordination",
    };
  }
  if (key === `persist:${userId}:email-config:v2`) return EMAIL_CONFIG_POLICY;
  if (key === `persist:${userId}:email-config:v1`) {
    return {
      id: "legacy-email-contact-config-v1",
      category: "legacy",
      scope: "user",
      version: 1,
      ttlMs: EMAIL_CONFIG_POLICY.ttlMs,
      maxBytes: EMAIL_CONFIG_POLICY.maxBytes,
      privacy: "private",
      clearOnLocalData: true,
      schema: "owner-bound unfenced contact config",
      defaultValue: null,
      migration: "migrate exact owner to generation-fenced v2 unless locally cleared",
      clearOn: ["logout-invalidation", "explicit-local-clear"],
      conflict: "reject",
      durability: "durable-with-visible-memory-fallback",
    };
  }
  const persistedPrefix = `persist:${userId}:`;
  if (key.startsWith(persistedPrefix)) {
    const logicalKey = key.slice(persistedPrefix.length) as PersistedStateKey;
    return PERSISTED_STATE_POLICIES[logicalKey] ?? null;
  }
  if (key.startsWith(`search_draft:${userId}:`) && key.endsWith(":search")) {
    return SEARCH_DRAFT_POLICY;
  }
  if (key.startsWith(`search_result:${userId}:`) && key.endsWith(":search")) {
    return SEARCH_RESULT_POLICY;
  }
  if (key.startsWith(`scroll:${userId}:search:`)) return SEARCH_SCROLL_POLICY;
  return null;
}

export function utf8Bytes(value: string): number {
  if (typeof TextEncoder !== "undefined") return new TextEncoder().encode(value).length;
  return value.length;
}

export function valueFitsPolicy(value: string, policy: PersistencePolicy): boolean {
  return utf8Bytes(value) <= policy.maxBytes;
}

export interface RegisteredValueEnvelope<T> {
  version: number;
  timestamp: number;
  value: T;
  ownerGeneration?: number;
  keyGeneration?: number;
}

export function serializeRegisteredValue<T>(
  value: T,
  policy: PersistencePolicy,
  timestamp = Date.now(),
  identity?: { ownerGeneration: number; keyGeneration: number }
): string | null {
  if (policy.validate && !policy.validate(value)) return null;
  const raw = JSON.stringify({
    version: policy.version,
    timestamp,
    value,
    ...(identity ?? {}),
  });
  return valueFitsPolicy(raw, policy) ? raw : null;
}

export function readRegisteredValue<T>(
  raw: string | null,
  policy: PersistencePolicy,
  now = Date.now(),
  expectedIdentity?: { ownerGeneration: number; keyGeneration: number }
): T | null {
  if (!raw || !valueFitsPolicy(raw, policy)) return null;
  try {
    const envelope = JSON.parse(raw) as Partial<RegisteredValueEnvelope<T>>;
    if (
      envelope.version !== policy.version ||
      !Number.isFinite(envelope.timestamp) ||
      (envelope.timestamp as number) > now + 5 * MINUTE ||
      now - (envelope.timestamp as number) >= policy.ttlMs ||
      (expectedIdentity &&
        (envelope.ownerGeneration !== expectedIdentity.ownerGeneration ||
          envelope.keyGeneration !== expectedIdentity.keyGeneration)) ||
      (policy.validate && !policy.validate(envelope.value))
    ) {
      return null;
    }
    return envelope.value as T;
  } catch {
    return null;
  }
}

/**
 * Identity-only janitor check for payload produced by the current bundle.
 * Used after a user has crossed a clear/logout generation so still-open older
 * bundles cannot recreate raw or generationless data behind the receipt.
 */
export function activeUserPayloadMatchesGeneration(
  key: string,
  raw: string,
  userId: string,
  ownerGeneration: number,
  keyGeneration: number,
  now = Date.now()
): boolean {
  const policy = classifyPersistenceKey(key, userId);
  if (!policy || !valueFitsPolicy(raw, policy)) return false;
  try {
    const envelope = JSON.parse(raw) as Record<string, unknown>;
    const timestamp = envelope.timestamp;
    if (
      !Number.isFinite(timestamp) ||
      (timestamp as number) > now + 5 * MINUTE ||
      now - (timestamp as number) >= policy.ttlMs
    ) return false;

    if (key === `persist:${userId}:email-config:v2`) {
      return envelope.version === EMAIL_CONFIG_POLICY.version &&
        envelope.userId === userId &&
        envelope.generation === ownerGeneration &&
        envelope.keyGeneration === keyGeneration;
    }
    if (key.startsWith(`persist:${userId}:`)) {
      return envelope.version === policy.version &&
        envelope.ownerGeneration === ownerGeneration &&
        envelope.generation === keyGeneration &&
        typeof envelope.value === "string";
    }
    if (
      key.startsWith(`search_draft:${userId}:`) ||
      key.startsWith(`search_result:${userId}:`) ||
      key.startsWith(`scroll:${userId}:`)
    ) {
      return envelope.version === policy.version &&
        envelope.ownerGeneration === ownerGeneration &&
        envelope.keyGeneration === keyGeneration;
    }
    return false;
  } catch {
    return false;
  }
}
